<script lang="ts">
	import { formatDateWithLocale } from '$lib/utils/date';
	import type { InputDateProps } from '../types';
	import { attrValue } from '$lib/utils/html';
	import Input from '../Input.svelte';
	import ButtonText from '../../buttons/variants/ButtonText.svelte';
	import Icon from '../../Icon.svelte';
	import { datepicker } from '$lib/actions';
	import { t } from 'svelte-i18n';
	import { French } from 'flatpickr/dist/l10n/fr.js';
	import 'flatpickr/dist/flatpickr.min.css';
	import { onDestroy, onMount } from 'svelte';

	export let props: InputDateProps;

	let isErrorDisplayed;
	let isFocus = false;

	let constOptions = {
		altInput: true,
		altFormat: 'd/m/Y',
		allowInvalidPreload: 'true',
		weekNumbers: true,
		locale: French,
		mode: props.mode ?? 'single',
		static: true
	};

	const handleChange = () => {
		if (!props.dirty) {
			props = { ...props, dirty: true };
		}
	};

	const handleClear = () => {
		props = { ...props, value: '' };
		props.ref.focus();
		props.ref._flatpickr.clear();
	};

	$: if (props) {
		props.endingIcon = 'calendar';
		props.placeholder = props.placeholder || formatDateWithLocale(new Date(), 'dd/MM/yyyy');
	}

	onMount(() => {
		if (props.options?.defaultDate) {
			props.value = props.options.defaultDate;
		}
		props && (datepickerOptions = { ...props.options, ...constOptions });
	});

	$: if (props && props.ref) {
		props.ref._flatpickr.config.onOpen.push(() => {
			isFocus = true;
			if (!props.touched) {
				props.touched = true;
			}
		});
		props.ref._flatpickr.config.onClose.push(() => (isFocus = false));
	}

	$: required = props && props.required;
	$: disabled = props && props.disabled;

	$: props.ref = props && props.ref ? props.ref : null;

	let datepickerOptions;

	onDestroy(() => {
		props.ref?._flatpickr.destroy();
	});
</script>

<Input bind:props bind:isErrorDisplayed>
	<div class="ctn flex">
		<div class="relative w-full">
			<input
				{required}
				{disabled}
				id={props.id}
				type="date"
				use:datepicker={datepickerOptions}
				name={props.name}
				placeholder={props.placeholder}
				class={attrValue(['input', props.classNames])}
				class:dirty={props.dirty}
				class:pl-6={props.leadingIcon}
				class:pr-6={props.endingIcon}
				class:disabled={props.disabled}
				class:border-danger-light={isErrorDisplayed}
				class:border-grey-light={!isErrorDisplayed}
				class:focus:border-danger={isErrorDisplayed}
				class:focus:border-secondary={!isErrorDisplayed}
				bind:value={props.value}
				bind:this={props.ref}
				on:input
				on:input|once={() => handleChange()}
				on:keypress
				on:keyup
			/>

			{#if props.leadingIcon}
				<Icon
					name={props.leadingIcon}
					classNames={attrValue([
						'transition absolute pl-3 top-1/2 transform -translate-y-1/2 left-0',
						isFocus ? 'text-secondary' : 'text-grey-light'
					])}
					size="big"
				/>
			{/if}

			{#if props.endingIcon}
				<Icon
					name={props.endingIcon}
					classNames={attrValue([
						'transition absolute pr-3 top-1/2 transform -translate-y-1/2 right-0',
						isFocus ? 'text-secondary' : 'text-grey-light'
					])}
					size="big"
				/>
			{/if}
		</div>

		{#if props.withClearAction}
			<ButtonText
				props={{
					size: 'small',
					iconLeft: 'cross',
					tooltip: {
						title: $t('clearField')
					}
				}}
				on:click={() => handleClear()}
			/>
		{/if}
	</div>
</Input>

<style lang="postcss">
	.ctn :global(.flatpickr-wrapper) {
		@apply w-full;
	}

	.ctn :global(.flatpickr-calendar .flatpickr-months .flatpickr-prev-month:hover svg),
	.ctn :global(.flatpickr-calendar .flatpickr-months .flatpickr-next-month:hover svg) {
		fill: theme('colors.primary');
	}

	.ctn :global(.flatpickr-calendar .flatpickr-day.selected),
	.ctn :global(.flatpickr-calendar .flatpickr-day.startRange),
	.ctn :global(.flatpickr-calendar .flatpickr-day.endRange),
	.ctn :global(.flatpickr-calendar .flatpickr-day.selected.inRange),
	.ctn :global(.flatpickr-calendar .flatpickr-day.startRange.inRange),
	.ctn :global(.flatpickr-calendar .flatpickr-day.endRange.inRange),
	.ctn :global(.flatpickr-calendar .flatpickr-day.selected:focus),
	.ctn :global(.flatpickr-calendar .flatpickr-day.startRange:focus),
	.ctn :global(.flatpickr-calendar .flatpickr-day.endRange:focus),
	.ctn :global(.flatpickr-calendar .flatpickr-day.selected:hover),
	.ctn :global(.flatpickr-calendar .flatpickr-day.startRange:hover),
	.ctn :global(.flatpickr-calendar .flatpickr-day.endRange:hover),
	.ctn :global(.flatpickr-calendar .flatpickr-day.selected.prevMonthDay),
	.ctn :global(.flatpickr-calendar .flatpickr-day.startRange.prevMonthDay),
	.ctn :global(.flatpickr-calendar .flatpickr-day.endRange.prevMonthDay),
	.ctn :global(.flatpickr-calendar .flatpickr-day.selected.nextMonthDay),
	.ctn :global(.flatpickr-calendar .flatpickr-day.startRange.nextMonthDay),
	.ctn :global(.flatpickr-calendar .flatpickr-day.endRange.nextMonthDay) {
		background: theme('colors.primary');
		border-color: theme('colors.primary');
	}

	.ctn
		:global(
			.flatpickr-calendar .flatpickr-day.selected.startRange + .endRange:not(:nth-child(7n + 1))
		),
	.ctn
		:global(
			.flatpickr-calendar .flatpickr-day.startRange.startRange + .endRange:not(:nth-child(7n + 1))
		),
	.ctn
		:global(
			.flatpickr-calendar .flatpickr-day.endRange.startRange + .endRange:not(:nth-child(7n + 1))
		) {
		-webkit-box-shadow: -10px 0 0 theme('colors.primary');
		box-shadow: -10px 0 0 theme('colors.primary');
	}

	.ctn :global(.flatpickr-calendar .flatpickr-day.week.selected) {
		border-radius: 0;
		-webkit-box-shadow:
			-5px 0 0 theme('colors.primary'),
			5px 0 0 theme('colors.primary');
		box-shadow:
			-5px 0 0 theme('colors.primary'),
			5px 0 0 theme('colors.primary');
	}
</style>
